/**
 * GameDrawer Component
 *
 * Side drawer for navigation, profile switching, and settings access.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { tokens } from '../../styles/tokens';
import { Profile } from '../../types';
import styles from './GameDrawer.module.scss';

interface GameDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  profiles?: Profile[];
  activeProfile?: Profile | null;
  onProfileSelect?: (profileId: string) => void;
}

export const GameDrawer: React.FC<GameDrawerProps> = ({
  isOpen,
  onClose,
  profiles = [],
  activeProfile,
  onProfileSelect,
}) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Close the drawer on Escape while it's open.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleProfileSelect = (profileId: string) => {
    onProfileSelect?.(profileId);
    onClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignOutConfirmed = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
      setConfirmingSignOut(false);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            className={styles.drawer}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className={styles.header}>
              <h2 className={styles.title}>Menu</h2>
              <button
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close menu"
                type="button"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Profiles Section */}
            {profiles.length > 0 && (
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Switch Profile</h3>
                <div className={styles.profileList}>
                  {profiles.map((profile) => (
                    <button
                      key={profile.id}
                      className={`${styles.profileItem} ${profile.id === activeProfile?.id ? styles.active : ''}`}
                      onClick={() => handleProfileSelect(profile.id)}
                    >
                      <div
                        className={styles.profileAvatar}
                        style={{ background: tokens.purpleBlue }}
                      >
                        {profile.display_name.charAt(0).toUpperCase()}
                      </div>
                      <span className={styles.profileName}>{profile.display_name}</span>
                      {profile.id === activeProfile?.id && (
                        <span className={styles.activeBadge}>Active</span>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Navigation Section */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Navigation</h3>
              <nav className={styles.navList}>
                <button
                  className={styles.navItem}
                  onClick={() => handleNavigation('/app/profile')}
                >
                  <span className={styles.navIcon}>👤</span>
                  Profile & Children
                </button>
                <button
                  className={styles.navItem}
                  onClick={() => handleNavigation('/app/settings')}
                >
                  <span className={styles.navIcon}>⚙️</span>
                  Settings
                </button>
              </nav>
            </section>

            {/* Sign Out */}
            <div className={styles.footer}>
              {confirmingSignOut ? (
                <div role="alertdialog" aria-label="Confirm sign out">
                  <p
                    style={{
                      margin: '0 0 12px',
                      fontSize: '0.95rem',
                      color: 'inherit',
                      textAlign: 'center',
                    }}
                  >
                    Sign out of MyMinu?
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className={styles.signOutButton}
                      style={{ flex: 1 }}
                      onClick={() => setConfirmingSignOut(false)}
                      disabled={signingOut}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.signOutButton}
                      style={{
                        flex: 1,
                        background: 'currentColor',
                        color: 'white',
                      }}
                      onClick={handleSignOutConfirmed}
                      disabled={signingOut}
                      autoFocus
                    >
                      {signingOut ? 'Signing out…' : 'Sign Out'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.signOutButton}
                  onClick={() => setConfirmingSignOut(true)}
                >
                  Sign Out
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GameDrawer;
